import './toolbar-plugin.scss';

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  BlockOptionsDropdownList,
  BlockType,
} from '../../elements/block-options-dropdown-list/block-options-dropdown-list';
import { EditorBlockButton } from '../../elements/editor-block-button/editor-block-button';
import { EditorToolBarDivider } from '../../elements/editor-toolbar-divider/editor-toolbar-divider';
import { FloatingLinkEditor } from '../../elements/link-editor/link-editor';
import { getSelectedNode } from '../../utils/get-selected-node';

const LowPriority = 1;

type ToolbarPluginProps = {
  blockTypes?: BlockType[];
};

export function ToolbarPlugin({ blockTypes }: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState<BlockType>(BlockType.PARAGRAPH); // 'paragraph'
  const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] =
    useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode
          );
          const listType = parentList
            ? parentList.getListType()
            : element.getListType();

          switch (listType) {
            case 'number':
              setBlockType(BlockType.NUMBER_LIST);
              break;
            case 'bullet':
              setBlockType(BlockType.BULLET_LIST);
              break;
            case 'check':
              setBlockType(BlockType.CHECK_LIST);
              break;
            default:
              setBlockType(BlockType.BULLET_LIST);
          }
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();

          const currentType = (Object.values(BlockType) as string[]).includes(
            type
          )
            ? (type as BlockType)
            : undefined;

          setBlockType(
            type === 'root'
              ? BlockType.PARAGRAPH
              : currentType ?? BlockType.PARAGRAPH
          );
        }
      }
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, [editor]);

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            updateToolbar();
          });
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          _payload => {
            updateToolbar();
            return false;
          },
          LowPriority
        ),
        editor.registerCommand(
          CAN_UNDO_COMMAND,
          payload => {
            setCanUndo(payload);
            return false;
          },
          LowPriority
        ),
        editor.registerCommand(
          CAN_REDO_COMMAND,
          payload => {
            setCanRedo(payload);
            return false;
          },
          LowPriority
        )
      ),
    [editor, updateToolbar]
  );

  return (
    <div className="toolbar-plugin" ref={toolbarRef}>
      <EditorBlockButton
        isToggle={false}
        disabled={!canUndo}
        format="undo"
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
      />
      <EditorBlockButton
        isToggle={false}
        disabled={!canRedo}
        format="redo"
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
      />
      <EditorToolBarDivider />

      <EditorBlockButton
        isToggle={false}
        format={blockType}
        text={blockType}
        onClick={() => {
          setShowBlockOptionsDropDown(!showBlockOptionsDropDown);
        }}
      />

      {showBlockOptionsDropDown &&
        createPortal(
          <BlockOptionsDropdownList
            options={blockTypes}
            editor={editor}
            selected={blockType}
            toolbar={toolbarRef.current}
            onClose={() => setShowBlockOptionsDropDown(false)}
          />,
          document.body
        )}
      <EditorToolBarDivider />
      <EditorBlockButton
        format="bold"
        active={isBold}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
      />
      <EditorBlockButton
        format="italic"
        active={isItalic}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
      />
      <EditorBlockButton
        format="underline"
        active={isUnderline}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        }}
      />
      <EditorBlockButton
        format="strikethrough"
        active={isStrikethrough}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }}
      />
      <EditorBlockButton
        onClick={() => {
          editor.dispatchCommand(
            TOGGLE_LINK_COMMAND,
            !isLink ? 'https://' : null
          );
        }}
        format="link"
      />

      {isLink &&
        createPortal(<FloatingLinkEditor editor={editor} />, document.body)}

      {/*
       <EditorToolBarDivider />
      <EditorBlockButton
        format="align-left"
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
        }}
      />
      <EditorBlockButton
        format="align-center"
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
        }}
      />
      <EditorBlockButton
        format="align-right"
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
        }}
      />

      <EditorBlockButton
        format="align-justify"
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
        }}
      /> */}
    </div>
  );
}
